import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from '../../services/account.service';
import { DynamicForm } from '../../models/dynamic.model';
import { BaseComponent } from '../../models/base/base.component';
import { MessageService } from 'primeng/api';
import { NavigationService } from '../../services/navigation.service';
import { FormGroup } from '@angular/forms';
import { EncryptionService } from '../../services/encryption.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-otp-verification',
  templateUrl: './otp-verification.component.html',
  styleUrls: ['../../styles/otp-verification.css']
})
export class OtpVerificationComponent extends BaseComponent implements OnInit {
  form: DynamicForm = new DynamicForm();
  resendDisabled: boolean = false;
  countdown: number = 30;
  countdownInterval: any;
  userId:string = localStorage.getItem('userId')
  isTfaPhoneVerified: boolean = localStorage.getItem('isTfaPhoneVerified') === 'true';
  private destroy$ = new Subject<void>();
  @ViewChild('dynamicForm') dynamicForm: any;

  constructor(
    private accountService: AccountService,
    protected router: Router,
    private navigationService: NavigationService,
    private messageService: MessageService,
    private encryptionService: EncryptionService
  ) {
    super();

    this.form.title = 'Verify OTP';
    this.form.key = 'otpForm';
    this.form.submitLabel = 'Verify OTP';
    this.form.autocomplete = true;

    this.form.addRow();
    const otpControl = this.form.addControl('otp', 'otp', 'OTP', null, true, null, 6);
    otpControl.minLength = 6;
    otpControl.maxLength = 6;
  }

  ngOnInit() {
    this.navigationService.showTopNav(false);
    this.accountService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.navigationService.showTopNav(true);
          this.router.navigate(['/orders']);
          return;
        }
      });
  }
  
  verifyCode() {
    if (this.dynamicForm && this.dynamicForm.form) {
      this.verifyOtp(this.dynamicForm.form);
    } else {
      this.verifyOtp(this.form);
    }
  }

  verifyOtp(form: any) {
    if (!form) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Form validation error',
        life: 5000
      });
      return;
    }
    
    const formValue = form instanceof FormGroup ? form.value : form.value || {};
    const otpCode = formValue.otp ? formValue.otp.toString().trim() : '';
    if (form instanceof FormGroup && form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please check the verification OTP and try again',
        life: 5000
      });
      return;
    }
    if (!otpCode || otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter a valid 6-digit verification OTP',
        life: 5000
      });
      return;
    }
    
    this.isLoading = true;

    const verificationData = this.isTfaPhoneVerified 
      ? { code: otpCode, username: localStorage.getItem('userName') || '' }
      : { code: otpCode, userId: +localStorage.getItem('userId') , key: localStorage.getItem('key')};

    const verificationMethod = this.isTfaPhoneVerified 
      ? this.accountService.verifyOtpForPhone(verificationData)
      : this.accountService.verifyOtpCodeForEmail(verificationData);

    verificationMethod.subscribe({
        next: (res) => {
          this.isLoading = false;
          
          if (res.isError) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: res.message || 'Failed to verify code. Please try again.',
              life: 5000
            });
            return;
          }
          
          this.navigationService.showTopNav(true);
          localStorage.setItem("isAdmin", res.body.isAdmin);
          localStorage.setItem("userRole", res.body.userRole);
          localStorage.setItem("userId", res.body.userId);
          localStorage.setItem("userName", res.body.userName);
          localStorage.setItem("email", res.body.email);
          localStorage.setItem("key", res.body.key);
          localStorage.setItem(
            "twoFactorVerified",
            res.body.twoFactorVerified,
          );
          localStorage.setItem(
            "userPermission",
            this.encryptionService.encryptObject(res.body.userPermission)
          );
          this.accountService.isAdminChanges(res.body.isAdmin);
          this.accountService.isLoginChanges(true);
          this.router.navigate([""]);
        },
        error: (err) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.message || 'Failed to verify code. Please try again.',
            life: 5000
          });
        }
      });
  }

  resendOtp() {
    if (this.resendDisabled) return;
    
    this.resendDisabled = true;
    this.countdown = 30;

    if (this.dynamicForm && this.dynamicForm.form) {
      this.dynamicForm.form.patchValue({ otp: '' });
    }
    
    const resendMethod = this.isTfaPhoneVerified 
      ? this.accountService.getResendAuthenticationCodeForPhone(localStorage.getItem('userName') || '')
      : this.accountService.getAuthenticationCodeForEmail(+(localStorage.getItem('userId') || '0'));

    resendMethod.subscribe({
      next: (res) => {
        if (res && res.body) {
          localStorage.setItem("key", res.body.key || '');
          this.messageService.add({
            severity: "success",
            summary: "Code Sent",
            detail: res?.body?.message || `A new OTP has been sent to your ${this.isTfaPhoneVerified ? 'mobile number' : 'email'}`,
            life: 5000,
          });
          
          // Start countdown
          this.startCountdown();
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to resend verification OTP. Please try again.',
          life: 5000
        });
        this.resendDisabled = false;
      }
    });
  }
  
  startCountdown() {
    this.countdown = 30;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.resendDisabled = false;
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}